<template>
  <v-navigation-drawer
    v-show="hasFranquia"
    :value="menuOpen"
    @input="handleMenuToggle"
    class="left-menu"
    width="260"
    app
  >
    <div class="menu-header">
      <div class="menu-header-glow"></div>
      <router-link :to="{ name: 'home' }" class="menu-logo-link">
        <img src="/img/logo-pontofiq-recorte.png" class="menu-logo" alt="Logo" />
      </router-link>
      <div class="menu-ano-selector">
        <e-ano-selector />
      </div>
    </div>

    <div class="menu-divider"></div>

    <v-list dense class="menu-list">
      <v-list-group
        v-for="item in visibleMenuItems()"
        :key="item.title"
        :id="item.id"
        v-model="item.active"
        no-action
        :append-icon="item.children && item.children.length > 0 ? undefined : ''"
        :prepend-icon="item.icon"
        @click="() => (item.to ? $router.push(item.to) : undefined)"
      >
        <template v-slot:activator>
          <v-list-item-content>
            <v-list-item-title v-text="item.title" />
          </v-list-item-content>
        </template>
        <template v-for="child in groupedChildren(item)">
          <div
            v-if="child.type === 'section'"
            :key="'section-' + child.title"
            class="menu-section-label"
          >
            {{ child.title }}
          </div>
          <v-list-item
            v-else
            :key="child.title"
            :id="child.id"
            :to="child.to"
            :href="child.href"
            :target="child.target"
          >
            <v-divider v-if="child.type === 'spacer'" />
            <v-list-item-content v-if="child.type !== 'spacer'">
              <v-list-item-title v-text="child.title" />
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-list-group>
    </v-list>

    <template v-slot:append>
      <div class="menu-footer">
        <v-card
          v-if="isSuperAdmin"
          class="menu-version-card menu-version-novidades"
          @click="$router.push({ name: 'novidades' })"
        >
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="d-flex align-center">
              <div class="menu-version-badge menu-version-badge-purple">
                <v-icon x-small>mdi-creation</v-icon>
              </div>
              <span class="menu-version-label">Novidades</span>
            </div>
            <v-chip class="menu-version-chip" x-small>
              {{ versaoAtual }}
            </v-chip>
          </div>
          <div class="menu-version-desc">Confira o histórico de atualizações</div>
        </v-card>

        <v-card v-else class="menu-version-card menu-version-static">
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="d-flex align-center">
              <div class="menu-version-badge menu-version-badge-muted">
                <v-icon x-small>mdi-information-outline</v-icon>
              </div>
              <span class="menu-version-label">Versão</span>
            </div>
            <v-chip class="menu-version-chip menu-version-chip-muted" x-small>
              {{ versaoAtual }}
            </v-chip>
          </div>
          <div class="menu-version-desc">Sistema em execução</div>
        </v-card>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters } from "vuex";
import EAnoSelector from "@/Pages/Templates/components/EAnoSelector.vue";

export default {
  name: "MainTemplate",
  components: {
    EAnoSelector,
  },
  watch: {
    user: {
      immediate: true,
      handler(newUser) {
        if (newUser && !newUser?.franquia?.id) {
          this.$toast.danger("Você não possui uma franquia vinculada.");
        }
      },
    },
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    ...mapGetters("Franquias", ["franquias"]),
    ...mapGetters("MainTemplate", ["menuOpen"]),
    ...mapGetters("AppConfigurations", ["moduloAtivo"]),
    hasFranquia() {
      return !!this.user?.franquia?.id;
    },
    isSuperAdmin() {
      if (!this.user) return false;
      const roles = this.user.roles || [];
      const roleName = this.user.roleName || "";
      return roles.some((r) => r.name === "super-admin") || roleName === "super-admin";
    },
    menu() {
      return {
        items: [
          {
            id: "menu.inicio",
            icon: "mdi-home",
            title: "Início",
            exibir: true,
            children: [
              {
                id: "menu.ir_para_inicio",
                title: "Ir para Início",
                to: { name: "home" },
                icon: "",
              },
            ],
          },
          {
            id: "menu.chat",
            icon: "mdi-message-text-outline",
            title: "Mensagens",
            exibir: this.hasUserRole(["super-admin", "admin", "TI", "colaborador", "lider"]),
            children: [
              {
                id: "menu.chat.conversas",
                title: "Conversas",
                to: { name: "chat.conversas" },
                modulo: "mensagens",
                icon: "",
              },
            ],
          },
          {
            icon: "mdi-clock-edit-outline",
            title: "Ponto",
            id: "menu.folha_de_pontos",
            exibir: true,
            children: [
              {
                title: "Meus Pontos",
                section: "Meu Tempo",
                id: "menu.folha_de_pontos.meus_pontos",
                to: { name: "colaboradores.meusPontos" },
                modulo: "meus_pontos",
                exibir: this.canAccessColaboradorModule(),
                permissions: ["colaboradores.meusPontos"],
              },
              {
                title: "Meu Banco de Horas",
                section: "Meu Tempo",
                id: "menu.folha_de_pontos.meu_banco_horas",
                to: { name: "colaboradores.bancoHoras" },
                modulo: "meu_banco_horas",
                exibir: this.canAccessColaboradorModule(),
              },
              {
                title: "Minhas Férias",
                section: "Meu Tempo",
                id: "menu.folha_de_pontos.minhas_ferias",
                to: { name: "colaboradores.minhasFerias" },
                modulo: "minhas_ferias",
                exibir: this.canAccessColaboradorModule(),
              },
              {
                title: "Justificar Falta",
                section: "Solicitações",
                to: { name: "colaboradores.faltasJustificadas" },
                modulo: "justificar_falta",
                exibir: this.canAccessColaboradorModule(),
                permissions: ["colaboradores.faltasJustificadas.solicitar"],
              },
              {
                title: "Justificar Atraso",
                section: "Solicitações",
                to: { name: "colaboradores.atrasosJustificados" },
                modulo: "justificar_atraso",
                exibir: this.canAccessColaboradorModule(),
                permissions: ["colaboradores.atrasosJustificados.solicitar"],
              },
              {
                title: "Registro Remoto",
                section: "Solicitações",
                to: { name: "colaboradores.batidasRemotas" },
                modulo: "registro_remoto",
                exibir: this.canAccessColaboradorModule(),
                permissions: ["colaboradores.batidasRemotas.solicitar"],
              },
              {
                title: "Ponto dos Colaboradores",
                section: "Gestão",
                id: "menu.folha_de_pontos.ponto_dos_colaboradores",
                to: { name: "colaboradores.pontos" },
                modulo: "ponto_colaboradores",
                exibir: ["admin", "super-admin", "TI", "lider"].includes(this.user?.roleName),
                permissions: ["colaboradores.pontos"],
              },
              {
                title: "Gerenciar Banco de Horas",
                section: "Gestão",
                id: "menu.folha_de_pontos.gerenciar_banco_horas",
                to: { name: "colaboradores.bancoHoras.gerenciar" },
                modulo: "gestao_banco_horas",
                exibir: ["admin", "super-admin", "TI", "lider"].includes(this.user?.roleName),
              },
              {
                title: "Gerenciar Férias",
                section: "Gestão",
                id: "menu.folha_de_pontos.gerenciar_ferias",
                to: { name: "colaboradores.ferias.gerenciar" },
                modulo: "gestao_ferias",
                exibir: ["admin", "super-admin", "TI", "lider"].includes(this.user?.roleName),
              },
              {
                title: "Gerenciar Justificativas",
                section: "Aprovações",
                to: { name: "colaboradores.faltasJustificadas.gerenciar" },
                modulo: "aprovar_faltas",
                exibir: ["admin", "super-admin", "TI", "lider"].includes(this.user?.roleName),
                permissions: ["colaboradores.faltasJustificadas.gerenciar"],
              },
              {
                title: "Gerenciar Atrasos",
                section: "Aprovações",
                to: { name: "colaboradores.atrasosJustificados.gerenciar" },
                modulo: "aprovar_atrasos",
                exibir: ["admin", "super-admin", "TI", "lider"].includes(this.user?.roleName),
                permissions: ["colaboradores.atrasosJustificados.gerenciar"],
              },
              {
                title: "Gerenciar Registros Remotos",
                section: "Aprovações",
                to: { name: "colaboradores.batidasRemotas.gerenciar" },
                modulo: "aprovar_remotos",
                exibir: ["admin", "super-admin", "TI", "lider"].includes(this.user?.roleName),
                permissions: ["colaboradores.batidasRemotas.gerenciar"],
              },
              {
                title: "Justificativa em Massa",
                section: "Ferramentas",
                id: "menu.folha_de_pontos.justificativa_em_massa",
                to: { name: "colaboradores.justificativaMassa" },
                modulo: "justificativa_massa",
                exibir: this.canAccessGestorModule(),
                permissions: ["colaboradores.justificativaMassa"],
              },
              {
                title: "Feriados",
                section: "Ferramentas",
                id: "menu.folha_de_pontos.feriados",
                to: { name: "feriados.gerenciar" },
                modulo: "feriados",
                permissions: ["feriados.gerenciar"],
              },
            ],
          },
          {
            id: "menu.avisos",
            icon: "mdi-bullhorn-outline",
            title: "Avisos",
            exibir: ["admin", "super-admin", "TI", "lider"].includes(this.user?.roleName),
            children: [
              {
                id: "menu.avisos.gerenciar",
                title: "Gerenciar Avisos",
                to: { name: "colaboradores.avisos.gerenciar" },
                modulo: "avisos",
                icon: "",
                permissions: ["colaboradores.avisos.gerenciar"],
              },
            ],
          },
          {
            id: "menu.usuarios",
            icon: "mdi-account-group-outline",
            title: "Colaboradores",
            exibir: this.hasUserRole(["admin", "super-admin", "lider"]),
            children: [
              {
                id: "menu.outros_usuarios",
                title: "Ver todos",
                to: { name: "usuarios" },
                icon: "fa fa-bookmark",
                permissions: ["users.*", "users.listar"],
              },
            ],
          },
          {
            id: "menu.administracao",
            icon: "mdi-cog-outline",
            title: "Administração",
            exibir: true,
            children: [
              {
                id: "menu.franquias",
                title: "Franquias",
                to: { name: "franquias" },
                permissions: ["franquias.*", "franquias.criarFranquia"],
                icon: "",
              },
              {
                // Central: módulos, aparelhos, registro remoto e notificações (ConfiguracoesPage)
                id: "menu.configuracoes",
                title: "Configurações",
                to: { name: "administracao.configuracoes" },
                exibir: this.hasUserRole(["admin", "super-admin", "TI", "lider"]),
                icon: "",
              },
            ],
          },
          {
            id: "menu.calendario_letivo",
            icon: "mdi-calendar-month-outline",
            title: "Calendário",
            exibir: true,
            children: [
              {
                id: "menu.calendario_letivo.calendario",
                title: "Calendário",
                to: { name: "calendario-letivo.create" },
                modulo: "calendario",
                permissions: ["calendarioLetivo.*", "calendarioLetivo.calendariosIndex"],
                icon: "",
              },
              {
                id: "menu.calendario_letivo.adicionar_categoria",
                title: "Adicionar Categoria",
                to: { name: "eventos-calendario-letivo" },
                modulo: "calendario",
                permissions: ["calendarioLetivo.*", "eventosCalendarioLetivo.eventosCreate"],
                icon: "",
              },
            ],
          },
          {
            id: "menu.relatorios",
            icon: "mdi-chart-box-outline",
            title: "Relatórios",
            exibir: true,
            children: [
              {
                id: "menu.relatorios.ponto_diario",
                title: "Relatório de Ponto Diário",
                section: "Gestão",
                to: { name: "relatorios.pontoDiario" },
                modulo: "ponto_colaboradores",
                exibir: ["admin", "super-admin", "TI", "lider"].includes(this.user?.roleName),
                permissions: ["colaboradores.pontos"],
              },
              {
                id: "menu.ver_todos_relatorios",
                title: "Ver todos",
                to: {
                  name: `relatorios`,
                  params: { franquiaId: this.user?.franquia?.id || 1 },
                },
                modulo: "relatorios",
                icon: "",
                permissions: ["relatorios.*", "relatorios.visualizar"],
              },
            ],
          },
        ],
        width: "250px",
      };
    },
  },
  data() {
    return {
      selectedItem: 0,
      openAtualizarSenhaModal: false,
      openSelecionarFranquiaModal: false,
      versaoAtual: "",
    };
  },
  async created() {
    try {
      const resp = await this.$services.novidadesService.getPublicReleases({ per_page: 1 });
      if (resp && resp.data && resp.data.length > 0) {
        this.versaoAtual = resp.data[0].versao;
      }
    } catch (e) {
      this.versaoAtual = "";
    }
  },
  methods: {
    handleMenuToggle(newValue) {
      if (newValue && !this.hasFranquia) {
        this.$toast.danger("Você não possui uma franquia vinculada.");
        return;
      }
      this.$store.commit("MainTemplate/setMenuOpen", newValue);
    },

    toggleMenu() {
      this.handleMenuToggle(!this.menuOpen);
    },

    canAccessMenuChild(child) {
      if (child.exibir === false) return false;
      if (!this.moduloAtivo(child.modulo)) return false;
      if (this.user?.roleName === "super-admin") return true;
      return this.$can(child.permissions);
    },
    visibleMenuItems() {
      return this.menu.items.filter((item) => item.exibir && this.groupedChildren(item).length > 0);
    },
    groupedChildren(item) {
      const children = [];
      let lastSection = "";
      (item.children || []).forEach((child) => {
        if (!this.canAccessMenuChild(child)) return;
        const section = child.section || "";
        if (section !== lastSection) {
          lastSection = section;
          if (section) {
            children.push({ type: "section", title: section });
          }
        }
        children.push(child);
      });
      return children;
    },
    canAccessInstructorAdminScheduleModule() {
      const allowedRoles = ["TI", "admin", "super-admin"];
      return allowedRoles.includes(this.user?.roleName);
    },
    hasUserRole(allowedRoles = []) {
      if (!this.user) return false;
      const userRoles = (this.user.roles || []).map((r) => r.name);
      if (this.user.roleName) userRoles.push(this.user.roleName);
      return allowedRoles.some((r) => userRoles.includes(r));
    },
    canAccessColaboradorModule() {
      const allowedRoles = ["colaborador", "lider", "admin", "super-admin", "TI"];
      return this.hasUserRole(allowedRoles);
    },
    canAccessGestorModule() {
      const allowedRoles = ["TI", "admin", "super-admin", "lider"];
      return this.hasUserRole(allowedRoles);
    },
  },
};
</script>

<style scoped>
/* ===== FUNDO & ESTRUTURA ===== */
.left-menu {
  background: linear-gradient(
    180deg,
    var(--surface-2) 0%,
    var(--surface) 15%,
    var(--surface) 100%
  ) !important;
  border-right: 1px solid var(--border) !important;
}

/* ===== HEADER ===== */
.menu-header {
  padding: 28px 16px 12px;
  position: relative;
  text-align: center;
  overflow: hidden;
}

.menu-header-glow {
  position: absolute;
  top: -50px;
  left: 50%;
  transform: translateX(-50%);
  width: 200px;
  height: 160px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(124, 58, 237, 0.1), transparent 70%);
  pointer-events: none;
}

.menu-logo-link {
  display: inline-block;
  position: relative;
  z-index: 1;
  transition: transform 0.2s ease;
}

.menu-logo-link:hover {
  transform: scale(1.04);
}

/* O logo é roxo escuro: no modo escuro ganha uma placa clara, como na tela de login. */
.theme--dark .menu-logo-link {
  padding: 8px 14px;
  border-radius: 14px;
  background: linear-gradient(145deg, #f5f0ff 0%, #ffffff 100%);
}

.menu-logo {
  display: block;
  margin: 0 auto;
  height: 110px;
  width: auto;
  object-fit: contain;
}

.menu-ano-selector {
  position: relative;
  z-index: 1;
  margin-top: 8px;
  display: flex;
  justify-content: center;
}

/* ===== DIVISOR ===== */
.menu-divider {
  height: 2px;
  margin: 4px 20px 8px;
  background: linear-gradient(90deg, transparent, rgba(124, 58, 237, 0.18), transparent);
  border: none;
}

/* ===== LISTA DO MENU ===== */
.menu-list {
  padding: 4px 8px !important;
}

/* Cabeçalhos dos grupos */
.left-menu >>> .v-list-group__header {
  border-radius: 10px !important;
  margin: 2px 0;
  min-height: 40px !important;
  transition: background-color 0.18s ease, transform 0.15s ease !important;
  cursor: pointer;
}

.left-menu >>> .v-list-group__header:hover {
  background-color: rgba(124, 58, 237, 0.06) !important;
  transform: translateX(2px);
}

.left-menu >>> .v-list-group__header .v-list-item__icon {
  color: var(--text-faint) !important;
  transition: color 0.18s ease;
}

.left-menu >>> .v-list-group__header:hover .v-list-item__icon {
  color: var(--brand-3) !important;
}

.left-menu >>> .v-list-group__header .v-list-item__title {
  font-weight: 600;
  font-size: 13.5px;
  color: var(--text-2) !important;
}

/* Grupo ativo (expandido) */
.left-menu >>> .v-list-group__header--active {
  color: var(--brand) !important;
}

.left-menu >>> .v-list-group__header--active .v-list-item__icon {
  color: var(--brand) !important;
}

/* Ícone de expandir */
.left-menu >>> .v-list-group__header .v-list-group__header__append-icon {
  color: var(--text-faint) !important;
  transition: transform 0.25s ease;
}

/* Itens dentro do grupo */
.left-menu >>> .v-list-group__items {
  padding-left: 4px;
}

.menu-section-label {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.9px;
  text-transform: uppercase;
  color: var(--brand-3);
  padding: 10px 0 6px 20px;
  line-height: 1;
  display: flex;
  align-items: center;
}

.menu-section-label::before {
  content: "";
  width: 3px;
  height: 10px;
  border-radius: 2px;
  background: linear-gradient(180deg, #7c3aed, #a78bfa);
  margin-right: 7px;
  flex-shrink: 0;
}

.left-menu >>> .v-list-group__items .v-list-item {
  border-radius: 8px !important;
  margin: 1px 0;
  min-height: 36px !important;
  transition: all 0.18s ease !important;
}

.left-menu >>> .v-list-group__items .v-list-item:hover {
  background-color: rgba(124, 58, 237, 0.06) !important;
}

.left-menu >>> .v-list-group__items .v-list-item .v-list-item__title {
  font-size: 13px;
  color: var(--text-3) !important;
  font-weight: 500;
  transition: color 0.18s ease;
}

.left-menu >>> .v-list-group__items .v-list-item:hover .v-list-item__title {
  color: var(--brand) !important;
}

/* Item ativo (rota atual) */
.left-menu >>> .v-list-group__items .v-list-item--active {
  background: linear-gradient(90deg, var(--brand-soft-2), transparent) !important;
}

.left-menu >>> .v-list-group__items .v-list-item--active .v-list-item__title {
  color: var(--brand) !important;
  font-weight: 700;
}

.left-menu >>> .v-list-group__items .v-list-item--active .v-list-item__icon {
  color: var(--brand) !important;
}

/* ===== RODAPÉ ===== */
.menu-footer {
  padding: 12px;
  border-top: 1px solid var(--border-soft);
  background: linear-gradient(180deg, transparent, var(--surface-2));
}

.menu-version-card {
  border-radius: 14px !important;
  padding: 12px !important;
  overflow: hidden;
  transition: all 0.2s ease !important;
  position: relative;
}

.menu-version-novidades {
  background: linear-gradient(135deg, var(--surface) 0%, var(--brand-soft) 100%) !important;
  border: 1px solid rgba(124, 58, 237, 0.18) !important;
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.06) !important;
  cursor: pointer;
}

.menu-version-novidades:hover {
  border-color: rgba(124, 58, 237, 0.35) !important;
  box-shadow: 0 8px 20px rgba(124, 58, 237, 0.12) !important;
  transform: translateY(-1px);
}

.menu-version-static {
  background: var(--surface-2) !important;
  border: 1px solid var(--border) !important;
  box-shadow: none !important;
}

.menu-version-badge {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  color: #ffffff;
  flex-shrink: 0;
}

.menu-version-badge-purple {
  background: linear-gradient(135deg, #4f008f, #7c3aed);
  box-shadow: 0 3px 8px rgba(79, 0, 143, 0.3);
}

.menu-version-badge-muted {
  background: linear-gradient(135deg, #94a3b8, #cbd5e1);
}

.menu-version-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--text-2);
}

.menu-version-chip {
  background: linear-gradient(135deg, #4f008f, #6d28d9) !important;
  color: #ffffff !important;
  font-weight: 800 !important;
  font-size: 11px !important;
  letter-spacing: 0.3px;
  box-shadow: 0 3px 8px rgba(79, 0, 143, 0.25);
}

.menu-version-chip-muted {
  background: linear-gradient(135deg, #64748b, #94a3b8) !important;
  box-shadow: none;
}

.menu-version-desc {
  font-size: 11px;
  color: var(--text-muted);
  padding-left: 4px;
  line-height: 1.4;
}
</style>
